import { bytesToHex, hmacSha256Bytes, sha256Bytes } from "@/service/hashFallback";

const SIGN_VERSION = "v1";

// 与后端白名单保持一致：这些接口不要求签名（登录/文档等）
const EXCLUDE_PATH_PREFIXES = [
  "/auth/email/user-login",
  "/auth/public-key",
  "/ai-video/sms/send",
  "/ai-video/sms/verify",
  "/ai-video/sms/reset/send",
  "/ai-video/sms/reset/confirm",
  "/ai-video/public/static-resources",
  "/doc.html",
  "/webjars/",
  "/swagger-resources/",
  "/v2/api-docs",
];

function sortKeys(value: unknown): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(sortKeys);
  }
  const sorted: Record<string, unknown> = {};
  Object.keys(value as Record<string, unknown>)
    .sort()
    .forEach((key) => {
      sorted[key] = sortKeys((value as Record<string, unknown>)[key]);
    });
  return sorted;
}

export function canonicalizeBody(method: string, data: unknown): string {
  // 与后端规则保持一致：GET/DELETE 不参与 body 签名
  const upperMethod = method.toUpperCase();
  if (upperMethod === "GET" || upperMethod === "DELETE") {
    return "";
  }
  if (data === undefined || data === null) {
    return "";
  }
  if (typeof data === "string") {
    // 已经是字符串时直接使用，避免双重 JSON.stringify 造成摘要不一致
    return data;
  }
  if (data instanceof FormData || data instanceof Blob || data instanceof ArrayBuffer) {
    return "";
  }
  return JSON.stringify(sortKeys(data));
}

export function canonicalizeQuery(params: Record<string, unknown> | undefined | null): string {
  if (!params) {
    return "";
  }
  const sortedKeys = Object.keys(params).sort();
  const pairs: string[] = [];
  for (const key of sortedKeys) {
    const value = params[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      const values = [...value].map((item) => (item == null ? "" : String(item))).sort();
      for (const item of values) {
        pairs.push(`${encodeQueryComponent(key)}=${encodeQueryComponent(item)}`);
      }
    } else {
      pairs.push(`${encodeQueryComponent(key)}=${encodeQueryComponent(String(value))}`);
    }
  }
  return pairs.join("&");
}

/** 与 qs 默认 RFC 3986 格式及后端 RequestSignUtils 保持一致。 */
function encodeQueryComponent(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
}

export function extractSignPath(url: string): string {
  let path = url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    path = new URL(url).pathname;
  } else {
    const queryIndex = path.indexOf("?");
    if (queryIndex >= 0) {
      path = path.substring(0, queryIndex);
    }
  }
  if (path.startsWith("/AiVideo")) {
    // 前端代理前缀不参与签名，后端看到的路径不含该前缀
    path = path.substring("/AiVideo".length) || "/";
  }
  if (path.endsWith(".json")) {
    path = path.substring(0, path.length - 5);
  }
  return path;
}

export function shouldSkipSign(path: string, token: string | null, signKey: string | null): boolean {
  // token/signKey 缺失时直接跳过，避免构造无效签名头
  if (!token || !signKey) {
    return true;
  }
  return EXCLUDE_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
}

export function generateNonce(): string {
  // 128-bit 随机 nonce，URL-safe Base64 编码
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let binary = "";
  bytes.forEach((item) => {
    binary += String.fromCharCode(item);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  if (crypto.subtle) {
    const hash = await crypto.subtle.digest("SHA-256", data);
    return bytesToHex(new Uint8Array(hash));
  }
  return bytesToHex(sha256Bytes(data));
}

export async function hmacSha256Hex(key: string, text: string): Promise<string> {
  const encoder = new TextEncoder();
  if (crypto.subtle) {
    const cryptoKey = await crypto.subtle.importKey("raw", encoder.encode(key), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
    const signature = await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(text));
    return bytesToHex(new Uint8Array(signature));
  }
  return bytesToHex(hmacSha256Bytes(encoder.encode(key), encoder.encode(text)));
}

export function buildSigningString(
  method: string,
  path: string,
  queryCanonical: string,
  token: string,
  timestamp: string,
  nonce: string,
  bodyHash: string,
): string {
  return [method.toUpperCase(), path, queryCanonical, token, timestamp, nonce, bodyHash].join("\n");
}

export async function buildRequestSignHeaders(options: {
  method: string;
  url: string;
  params?: Record<string, unknown>;
  data?: unknown;
  token: string;
  signKey: string;
}): Promise<Record<string, string>> {
  // 注意：字段顺序必须与后端 buildSigningString 完全一致
  const method = (options.method || "GET").toUpperCase();
  const path = extractSignPath(options.url);
  const queryCanonical = canonicalizeQuery(options.params);
  const bodyCanonical = canonicalizeBody(method, options.data);
  const bodyHash = await sha256Hex(bodyCanonical);
  const timestamp = String(Date.now());
  const nonce = generateNonce();
  const signingString = buildSigningString(method, path, queryCanonical, options.token, timestamp, nonce, bodyHash);
  const signature = await hmacSha256Hex(options.signKey, signingString);
  return {
    "X-Sign-Timestamp": timestamp,
    "X-Sign-Nonce": nonce,
    "X-Body-Hash": bodyHash,
    "X-Signature": signature,
    "X-Sign-Version": SIGN_VERSION,
  };
}

export { SIGN_VERSION };
